import React, { useEffect, useState, useMemo } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, Shield, KeyRound, Mail, Lock, BadgeCheck, ShieldCheck, Award } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useTheme } from "@/lib/theme-context";
import { useBranding, getOrgUrlHints } from "@/lib/branding";
import { readAuthHero, writeAuthHero } from "@/lib/auth-hero-cache";
import { applyBranding } from "@/lib/settings-context";
import { captureLoginSnapshot } from "@/lib/theme-snapshot";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import TermsAgreementModal from "@/components/TermsAgreementModal";
import BrandHeroImage from "@/components/BrandHeroImage";

export default function Login() {
    const { user, login } = useAuth();
    const { setDefaultTheme } = useTheme();
    // iter-119h: brand identity flows through useBranding (env defaults +
    // tenant SA overrides). The "A" mark below is derived from the brand
    // first letter so a rebrand to "Lumiq" auto-renders an "L" mark.
    const platformBrand = useBranding();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);
    const [loading, setLoading] = useState(false);
    // iter — Mandatory Terms agreement (spec §2 / §3). Checkbox must be
    // agreed via the popup before Sign In / SSO buttons unlock. Cleared
    // on tab-load so an old session in the same browser can't skip it.
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [tosVersionId, setTosVersionId] = useState(null);
    const [tosModalOpen, setTosModalOpen] = useState(false);
    // iter — Legal document links (SA-configured PDFs) fetched from
    // /api/legal/links. Falls back to static SPA routes until SA
    // uploads real PDFs. Mirrors Signup.jsx behaviour.
    const [legalLinks, setLegalLinks] = useState({
        terms:   { url: "/terms",   is_pdf: false },
        privacy: { url: "/privacy", is_pdf: false },
    });
    // iter — SSO flow now opens the email prompt directly. Terms
    // agreement lives INSIDE the SSO email dialog (checkbox + inline
    // Terms link opens the full TOS modal). No pending-provider state
    // needed anymore — flow is linear: click SSO → enter email →
    // tick Terms → Continue.
    const [ssoAcceptedTerms, setSsoAcceptedTerms] = useState(false);
    const [ssoTosModalOpen, setSsoTosModalOpen] = useState(false);
    const [activeSession, setActiveSession] = useState(null); // {email, password}
    // iter-120 round-4 follow-up #13 — Tenant-aware login branding.
    // When the user types an email, we fetch the matching tenant's
    // branding via the public `/api/branding/lookup` endpoint and
    // override `app_name` + `logo_url` so OA tenants see their own
    // brand on the login page. Falls back to platform defaults.
    const [tenantBrand, setTenantBrand] = useState(null);
    // Platform DEFAULT branding (from the no-email /branding/lookup). Applied
    // immediately on mount so the SA-configured platform logo + name render
    // the moment the page opens — never gated on the user typing an email.
    const [platformDefaultBrand, setPlatformDefaultBrand] = useState(null);
    // Hero-flicker fix: `brandResolved` flips true once the pre-email
    // branding lookup has settled (success OR failure). Until then the hero
    // shows the last-known configured image for this site (local cache) or
    // a neutral panel — never the built-in gradient mock.
    const [brandResolved, setBrandResolved] = useState(false);
    const [cachedHero] = useState(() => readAuthHero(getOrgUrlHints()));
    // iter-120 round-4 follow-up #8 — SSO email-prompt modal state.
    const [ssoProvider, setSsoProvider] = useState(null);   // "microsoft"|"google"|null
    const [ssoEmail, setSsoEmail] = useState("");
    const [ssoLoading, setSsoLoading] = useState(false);
    // iter-120 round-4 follow-up #13 — Forgot Password modal state.
    // Replaces the toast-based UX which left users wondering what
    // happened (especially when SMTP isn't configured and a temp
    // password is returned inline).
    const [forgotOpen, setForgotOpen] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotBusy, setForgotBusy] = useState(false);
    const [forgotResult, setForgotResult] = useState(null); // {ok, email_sent, temporary_password, message}
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    // `?next=` return path — used by AcceptWorkspaceInvite etc. to bring the
    // user back after they sign in. Defaults to "/" to preserve existing UX.
    const nextPath = sp.get("next") || "/";

    // Merged brand: tenant override wins for app_name + logo, falls back
    // to platform defaults otherwise.
    // iter-121.101 — copyrightLine now composes `© {yr} {company}, Inc.
    // All rights reserved.` using the SA-set `auth_copyright_company`
    // (falls back to product app name — NEVER blank).
    const brand = useMemo(() => {
        // Effective PUBLIC branding source: a confirmed tenant override wins,
        // otherwise the platform default fetched on mount.
        const src = tenantBrand || platformDefaultBrand;
        // RAW configured brand name — null/empty when the SA left Brand Name
        // blank. NEVER fall back to a hardcoded product name for the wordmark
        // shown beside the logo (Issue 2).
        const configuredName = ((src?.app_name) || platformBrand.configuredAppName || "").trim();
        // Display name for body copy / copyright (safe fallback allowed here).
        const appName = configuredName || platformBrand.appName;
        const company = (src?.auth_copyright_company || "").trim() || appName;
        return {
            ...platformBrand,
            appName,
            configuredName,
            logoUrl: src?.logo_url || platformBrand.logoUrl,
            tagline: src?.tagline || platformBrand.tagline,
            // iter-2026 — hero image must fall back to the platform default
            // (no-email load stores it in platformDefaultBrand). `src` already
            // resolves tenant-override → platform-default.
            loginImageUrl: src?.login_image_url
                // Last-known saved image while the lookup is pending — and
                // also if it failed (no platform brand) so returning visitors
                // keep their image on a flaky network.
                || ((!brandResolved || !platformDefaultBrand?.login_image_url) ? (cachedHero?.login_image_url || null) : null),
            copyrightLine: () => `© ${new Date().getFullYear()} ${company}, Inc. All rights reserved.`,
        };
    }, [platformBrand, tenantBrand, platformDefaultBrand, brandResolved, cachedHero]);

    useEffect(() => {
        const reason = sessionStorage.getItem("analyzio_logout_reason");
        if (reason) {
            const isTakeover = reason.toLowerCase().includes("session");
            if (isTakeover) {
                toast("Signed in on your other device", {
                    description: "You've been signed out here. Sign in again to continue.",
                });
            } else {
                toast.warning(reason);
            }
            sessionStorage.removeItem("analyzio_logout_reason");
        }
    }, []);

    // iter — Fetch SA-configured legal PDF links once on mount.
    useEffect(() => {
        api.get("/legal/links").then((r) => {
            if (r.data && r.data.terms && r.data.privacy) {
                setLegalLinks(r.data);
            }
        }).catch(() => { /* keep default fallback */ });
    }, []);

    // iter-120 round-4 follow-up #13 — Debounced tenant branding lookup.
    // iter-121.98 — Also fetches platform-tenant defaults on initial load
    // (empty email) so the login hero image renders before the user types.
    // iter-121.128 — Track whether the user has actually touched the
    // email field. The field is pre-filled with the demo OA email, which
    // previously made the lookup IMMEDIATELY query for that tenant — so
    // freshly-promoted platform branding flashed in for ~500ms and then
    // got overwritten by the demo tenant's stale override. We now skip
    // the tenant-specific lookup on the first render and only run it once
    // the user actively interacts with the email field (or after the
    // initial platform fetch settles).
    const [userTouchedEmail, setUserTouchedEmail] = useState(false);
    // (1) PRE-EMAIL / platform branding — fetched ONCE on mount and never
    // cancelled by typing. Includes URL org hints (subdomain / ?org= /
    // configured Organization URL) so a shared login URL shows the right org
    // branding; the backend falls back to the platform default when
    // unmatched. Running this only once matters: the response can carry the
    // hero image inline (~1 MB), and re-firing it per keystroke delayed the
    // saved image and caused the gradient-hero flicker.
    useEffect(() => {
        let cancelled = false;
        const hints = getOrgUrlHints();
        const qs = new URLSearchParams();
        if (hints.slug) qs.set("slug", hints.slug);
        if (hints.host) qs.set("host", hints.host);
        api.get(`/branding/lookup${qs.toString() ? `?${qs.toString()}` : ""}`)
            .then((r) => {
                if (cancelled) return;
                setPlatformDefaultBrand(r.data || null);
                writeAuthHero(hints, r.data || null);
            })
            .catch(() => { /* keep cached hero / built-in fallback */ })
            .finally(() => { if (!cancelled) setBrandResolved(true); });
        return () => { cancelled = true; };
    }, []);

    // (2) Tenant override — only once the user has typed a FULL email
    // (debounced). A confirmed tenant override wins for logo + app name;
    // otherwise the platform branding from (1) stays in effect.
    useEffect(() => {
        const trimmed = (email || "").trim().toLowerCase();
        const hasFullEmail = trimmed && trimmed.includes("@") && trimmed.length >= 5;
        if (!(hasFullEmail && userTouchedEmail)) {
            setTenantBrand(null);
            return undefined;
        }
        let stale = false;
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/branding/lookup?email=${encodeURIComponent(trimmed)}`);
                if (!stale) setTenantBrand(r.data?.tenant_found ? r.data : null);
            } catch {
                if (!stale) setTenantBrand(null);
            }
        }, 500);
        return () => { stale = true; clearTimeout(t); };
    }, [email, userTouchedEmail]);

    // iter-121.130 — Push the platform/tenant branding from
    // `/branding/lookup` into the document (favicon, title, CSS vars
    // for primary/sidebar/font etc.) so the /login page renders the
    // SA-promoted theme immediately rather than inheriting whatever
    // the previous logged-in session left on the DOM. Pairs with the
    // `resetBrandingDom()` call in settings-context.jsx that fires on
    // logout — together they eliminate the "new image then back to
    // prior branding" flicker.
    useEffect(() => {
        // Apply the effective PUBLIC brand (tenant override, else platform
        // default) to the DOM so favicon/title/theme + logo paint on mount.
        const publicBrand = tenantBrand || platformDefaultBrand;
        if (publicBrand) {
            applyBranding(publicBrand);
            setDefaultTheme(publicBrand.theme_mode);
            const systemDark = publicBrand.theme_mode === "system"
                && window.matchMedia?.("(prefers-color-scheme: dark)").matches === true;
            captureLoginSnapshot({
                forceDark: publicBrand.theme_mode === "dark" || systemDark,
            });
        }
    }, [tenantBrand, platformDefaultBrand, setDefaultTheme]);

    // iter-120 round-4 follow-up #8/#9 — Show error toast when the SSO
    // callback redirected us back here with `?sso_error=...`.
    useEffect(() => {
        const err = sp.get("sso_error");
        if (err) {
            toast.error(decodeURIComponent(err));
        }
    }, [sp]);

    // iter-121.32 — Email pre-fill + welcome toast after invite acceptance.
    // The AcceptInvite page redirects here with ?email=...&from=invite once
    // the user has set their password. We pre-fill the email field + show
    // a contextual toast so the user knows what happened next (they need
    // to log in with the password they just set).
    const activationToastShown = React.useRef(false);
    useEffect(() => {
        const emailHint = sp.get("email");
        const from = sp.get("from");
        if (emailHint && from === "invite" && !activationToastShown.current) {
            activationToastShown.current = true;
            setEmail(emailHint);
            // Clear any auto-filled default password so the user has to
            // re-type the one they JUST set (verification step).
            setPassword("");
            toast.success("Account activated — sign in with the password you just set.");
        } else if (emailHint && from === "reset") {
            setEmail(emailHint);
            setPassword("");
        }
        // Run only when the search params change. Safe to depend on `sp`.
    }, [sp]);

    // iter-2026.02i — Validation state for the "*" required fields.
    // MUST live above the `if (user) return` early exit so React
    // Hook rules aren't violated when the user is already authed.
    const [emailErr, setEmailErr] = useState("");
    const [pwdErr, setPwdErr] = useState("");

    if (user) return <Navigate to={nextPath} replace />;

    const attempt = async ({ force = false } = {}) => {
        setLoading(true);
        try {
            await login(email, password, { force });
            toast.success(force ? "Other session signed out — welcome back" : "Welcome back");
            setActiveSession(null);
            navigate(nextPath);
        } catch (err) {
            const resp = err?.response;
            // The backend api_response.py global handler flattens dict details into the root.
            const payload = resp?.data || {};
            const detail = payload.detail;
            const code = payload.code || detail?.code;
            const msg = payload.message || detail?.message || (typeof detail === "string" ? detail : "Sign in failed");
            
            if (resp?.status === 409 && code === "active_session") {
                setActiveSession({ message: msg });
                return;
            }
            // iter-120 round-4 follow-up #13 — Surface the exact backend
            // message including the human-readable lockout text added
            // in this iteration. Distinguish 423 (locked) so the toast
            // uses error styling and longer duration.
            const isLocked = resp?.status === 423;
            toast.error(msg, isLocked ? { duration: 10000 } : undefined);
        } finally {
            setLoading(false);
        }
    };

    const validateLoginForm = () => {
        let ok = true;
        const trimmed = (email || "").trim();
        if (!trimmed) { setEmailErr("Email is required."); ok = false; }
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setEmailErr("Please enter a valid email address."); ok = false; }
        else setEmailErr("");
        if (!(password || "").trim()) { setPwdErr("Password is required."); ok = false; }
        else setPwdErr("");
        return ok;
    };

    const handle = async (e) => {
        e.preventDefault();
        if (!validateLoginForm()) return;
        await attempt();
    };

    const startSso = async (e) => {
        if (e?.preventDefault) e.preventDefault();
        if (!ssoEmail || !ssoProvider) return;
        console.log("[SSO-LOGIN] Starting SSO flow", { email: ssoEmail, provider: ssoProvider });
        setSsoLoading(true);
        try {
            console.log("[SSO-LOGIN] Calling authorize-url endpoint");
            const r = await api.post(`/auth/sso/${ssoProvider}/authorize-url`, { email: ssoEmail });
            const url = r.data?.url || r.data?.data?.url;
            console.log("[SSO-LOGIN] Authorize URL received:", url ? url.substring(0, 80) + "..." : "MISSING");
            if (!url) throw new Error("Authorize URL missing in response");
            console.log("[SSO-LOGIN] Redirecting to provider", { url: url.substring(0, 80) });
            window.location.href = url;
        } catch (err) {
            const data = err?.response?.data || {};
            console.error("[SSO-LOGIN] Error:", err?.response?.status, data.message || data.detail || err?.message);
            const detail = data.message || data.detail || err?.message || "Could not start SSO";
            toast.error(detail);
        } finally {
            setSsoLoading(false);
        }
    };

    // iter-121.7 — Forgot Password submit handler. The legacy "temp
    // password emailed in plaintext" UX has been replaced with a
    // reset-LINK flow: the backend issues a one-time, time-limited,
    // hashed-at-rest token and emails a link. SSO-only users are
    // short-circuited with a polite "use your IdP" message. The
    // success response is intentionally generic to prevent email
    // enumeration; for the demo path (SMTP not configured), the
    // backend additionally returns `demo_reset_url` so the operator
    // can still test the flow without a live SMTP server.
    const submitForgot = async (e) => {
        if (e?.preventDefault) e.preventDefault();
        const trimmed = (forgotEmail || "").trim().toLowerCase();
        if (!trimmed) { toast.error("Enter your email first"); return; }
        setForgotBusy(true);
        setForgotResult(null);
        try {
            const r = await api.post("/auth/reset-password", { email: trimmed });
            setForgotResult({
                ok: true,
                email_sent: !!r.data?.email_sent,
                sso_only: !!r.data?.sso_only,
                demo_reset_url: r.data?.demo_reset_url || null,
                email: trimmed,
                note: r.data?.note,
            });
        } catch (err) {
            const detail = err?.response?.data?.detail || "Reset failed. Please try again or contact your administrator.";
            setForgotResult({ ok: false, error: detail });
        } finally {
            setForgotBusy(false);
        }
    };

    return (
        <div className="min-h-screen w-full grid lg:grid-cols-[40%_60%] bg-background" data-testid="login-page">
            {/* Left: form */}
            <div className="flex flex-col px-8 lg:px-12 xl:px-16 py-10 pt-8 lg:pt-10">
                <div className="flex items-center gap-2.5 z-30 mb-4 max-w-md w-full mx-auto" data-testid="login-brand">
                    {brand.logoUrl ? (
                        // Logo configured → render it. Show a text wordmark
                        // ONLY when a Brand Name is actually configured; never
                        // print a hardcoded product name beside the logo.
                        <>
                            <img
                                src={brand.logoUrl}
                                alt={brand.configuredName || "Brand logo"}
                                className="h-12 w-auto max-w-[180px] object-contain"
                                data-testid="login-brand-logo"
                            />
                            {brand.configuredName && (
                                <span className="text-lg font-semibold tracking-tight" data-testid="login-brand-name">
                                    {brand.configuredName}
                                </span>
                            )}
                        </>
                    ) : (
                        // No logo → initials fallback mark. Wordmark text only
                        // when a Brand Name is configured (no hardcoded name).
                        <>
                            <div className="w-8 h-8 bg-primary flex items-center justify-center" data-testid="login-brand-mark">
                                <span className="font-mono text-primary-foreground font-semibold">{(brand.configuredName || brand.appName).charAt(0).toUpperCase()}</span>
                            </div>
                            {brand.configuredName && (
                                <span className="text-lg font-semibold tracking-tight" data-testid="login-brand-name">{brand.configuredName}</span>
                            )}
                        </>
                    )}
                </div>

                <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
                    <div className="inline-flex items-center gap-2 mb-4 self-start">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[10px] font-semibold tracking-wide uppercase" data-testid="login-enterprise-badge">
                            Enterprise Analytics
                        </span>
                    </div>
                    <h1 className="text-4xl font-semibold tracking-tight leading-[1.05] mb-2.5" data-testid="login-heading">
                        Welcome <span className="font-serif-display italic font-normal">back</span>
                    </h1>
                    <p className="text-sm text-muted-foreground mb-7">
                        Sign in to your workspace and continue to unified AI analytics.
                    </p>

                    {/* SSO buttons — full-width per screenshot */}
                    <div className="grid grid-cols-2 gap-2.5 mb-5">
                        <button
                            type="button"
                            className="h-11 border border-border rounded-md text-sm font-medium hover:border-foreground/40 transition-colors flex items-center justify-center gap-2.5 px-3"
                            data-testid="login-sso-microsoft"
                            onClick={() => {
                                // iter — Open SSO email dialog directly.
                                // Terms agreement now lives INSIDE that
                                // dialog (checkbox + inline link).
                                setSsoAcceptedTerms(false);
                                setSsoProvider("microsoft"); setSsoEmail(email || "");
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16"><rect width="7" height="7" fill="#F25022"/><rect x="8" width="7" height="7" fill="#7FBA00"/><rect y="8" width="7" height="7" fill="#00A4EF"/><rect x="8" y="8" width="7" height="7" fill="#FFB900"/></svg>
                            Sign in with Microsoft
                        </button>
                        <button
                            type="button"
                            className="h-11 border border-border rounded-md text-sm font-medium hover:border-foreground/40 transition-colors flex items-center justify-center gap-2.5 px-3"
                            data-testid="login-sso-google"
                            onClick={() => {
                                setSsoAcceptedTerms(false);
                                setSsoProvider("google"); setSsoEmail(email || "");
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2c-2.1 1.4-4.7 2.3-7.3 2.3-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.2 5.2c-.4.4 6.5-4.8 6.5-14.7 0-1.3-.1-2.3-.4-3.5z"/></svg>
                            Sign in with Google
                        </button>
                    </div>

                    <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center">
                            <span className="bg-background px-3 eyebrow">or continue with email</span>
                        </div>
                    </div>

                    <form onSubmit={handle} className="space-y-3">
                        <div>
                            <label className="eyebrow mb-1.5 block" htmlFor="login-email">
                                Email address <span className="text-destructive" aria-hidden="true">*</span>
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <input
                                    id="login-email"
                                    type="email"
                                    required
                                    aria-required="true"
                                    aria-invalid={!!emailErr}
                                    aria-describedby={emailErr ? "login-email-err" : undefined}
                                    value={email}
                                    onChange={(e) => { setEmail(e.target.value); setUserTouchedEmail(true); if (emailErr) setEmailErr(""); }}
                                    className={`w-full h-11 pl-9 pr-3 bg-transparent border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary ${emailErr ? "border-destructive" : "border-border"}`}
                                    data-testid="login-email-input"
                                    placeholder="you@company.com"
                                />
                            </div>
                            {emailErr && (
                                <p id="login-email-err" className="text-[11px] text-destructive mt-1" data-testid="login-email-error">{emailErr}</p>
                            )}
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="eyebrow" htmlFor="login-password">
                                    Password <span className="text-destructive" aria-hidden="true">*</span>
                                </label>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setForgotEmail(email || "");
                                        setForgotResult(null);
                                        setForgotOpen(true);
                                    }}
                                    className="text-[11px] text-primary hover:underline bg-transparent border-0 cursor-pointer p-0 font-medium"
                                    data-testid="login-forgot-link"
                                >
                                    Forgot password?
                                </button>
                            </div>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <input
                                    id="login-password"
                                    type={showPassword ? "text" : "password"}
                                    required
                                    aria-required="true"
                                    aria-invalid={!!pwdErr}
                                    aria-describedby={pwdErr ? "login-password-err" : undefined}
                                    value={password}
                                    onChange={(e) => { setPassword(e.target.value); if (pwdErr) setPwdErr(""); }}
                                    className={`w-full h-11 pl-9 pr-10 bg-transparent border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary font-mono ${pwdErr ? "border-destructive" : "border-border"}`}
                                    data-testid="login-password-input"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((s) => !s)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    data-testid="login-show-password"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {pwdErr && (
                                <p id="login-password-err" className="text-[11px] text-destructive mt-1" data-testid="login-password-error">{pwdErr}</p>
                            )}
                        </div>

                        {/* Remember me */}
                        <label className="flex items-center gap-2 text-sm cursor-pointer select-none pt-1" data-testid="login-remember-row">
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                className="h-4 w-4 rounded border-border accent-primary"
                                data-testid="login-remember-checkbox"
                            />
                            Remember me
                        </label>

                        {/* iter-2026.02i — Terms checkbox removed per spec:
                            Login is a return-user flow (they agreed on
                            signup). Terms/Privacy links stay reachable
                            via the SSO email popup and the Sign Up
                            page. */}

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 rounded-md font-medium mt-2"
                            data-testid="login-submit-button"
                        >
                            {loading ? "Signing in…" : <>Sign in <ArrowRight className="h-4 w-4 ml-1.5" /></>}
                        </Button>

                        <div className="text-xs text-center text-muted-foreground pt-1">
                            New to Analyzio? <Link to="/signup" className="underline hover:text-primary font-medium" data-testid="login-signup-link">Start a free trial</Link>
                        </div>
                    </form>

                    {/* Secure & Trusted card — replaces the demo-credentials chip
                        on the visible canvas (per screenshot). Demo creds are
                        kept below for QA but visually subdued. */}
                    <div className="mt-6 p-4 border border-primary/25 rounded-md bg-primary/[0.04]" data-testid="login-trust-card">
                        <div className="flex items-start gap-3">
                            <div className="h-9 w-9 rounded-md bg-primary/15 text-primary flex items-center justify-center shrink-0">
                                <ShieldCheck className="h-5 w-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold">Secure &amp; Trusted</div>
                                <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                                    Your data is encrypted, private, and protected with enterprise-grade security.
                                </div>
                               
                            </div>
                        </div>
                    </div>

                    {/* Demo credentials completely disabled per user request:
                    tenantBrand?.auth_show_demo_credentials !== false && (
                    <details className="mt-4 group" data-testid="login-demo-credentials">
                        <summary className="cursor-pointer text-[11px] text-muted-foreground hover:text-foreground font-mono uppercase tracking-wider list-none flex items-center gap-1.5">
                            <Shield className="h-3 w-3" /> Demo credentials
                            <span className="text-[9px] text-muted-foreground/70 normal-case ml-auto">click to autofill</span>
                        </summary>
                        <div className="mt-2 p-3 border border-border rounded-md bg-secondary/40">
                            <div className="grid grid-cols-1 gap-y-1 font-mono text-[11px]">
                                {[
                                    { e: "superadmin@analyzio.io", p: "SuperAdmin@2026", r: "super_admin" },
                                    { e: "admin@acme.io",          p: "Admin@2026",      r: "org_admin" },
                                    { e: "analyst@acme.io",        p: "Analyst@2026",    r: "entity_admin" },
                                    { e: "viewer@acme.io",         p: "Viewer@2026",     r: "viewer" },
                                ].map((c) => (
                                    <button key={c.e}
                                        type="button"
                                        onClick={() => { setEmail(c.e); setPassword(c.p); setUserTouchedEmail(true); }}
                                        className="text-left hover:bg-secondary px-1.5 py-0.5 -mx-1.5 rounded-sm transition-colors"
                                        data-testid={`login-demo-${c.r}`}>
                                        <span className="text-foreground">{c.e}</span>
                                        <span> · </span>
                                        <span>{c.p}</span>
                                        <span className="text-muted-foreground/70"> ({c.r})</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </details>
                    )*/}
                </div>

                {/* Footer — copyright only. Terms + Privacy links moved
                    to the mandatory agreement row above the Sign In button
                    (spec §2). */}
                <div className="text-[11px] text-muted-foreground pt-4 border-t border-border/40 text-center" data-testid="login-footer">
                    <span data-testid="login-copyright">{brand.copyrightLine()}</span>
                </div>
            </div>

            {/* Mandatory Terms popup for email login + inline "Terms"
                link in the email row. SSO uses its own instance (see
                below) so the flow stays: SSO click → email prompt →
                open TOS from checkbox row. */}
            <TermsAgreementModal
                open={tosModalOpen}
                onClose={() => setTosModalOpen(false)}
                onAgree={(vid) => {
                    setAcceptedTerms(true);
                    setTosVersionId(vid);
                    setTosModalOpen(false);
                }}
                policyType="terms_of_service"
                authMethod="email"
                email={email || null}
                recordAgree={true}
            />

            {/* SSO-scoped Terms modal — opens from the checkbox in
                the SSO email prompt. Agree ticks the checkbox and
                closes the TOS modal; the SSO prompt remains open so
                the user can click Continue. */}
            <TermsAgreementModal
                open={ssoTosModalOpen}
                onClose={() => setSsoTosModalOpen(false)}
                onAgree={() => {
                    setSsoAcceptedTerms(true);
                    setSsoTosModalOpen(false);
                }}
                policyType="terms_of_service"
                authMethod={ssoProvider || "email"}
                email={ssoEmail || null}
                recordAgree={true}
            />

            {/* Right: hero — iter-121.100 — uploaded login_image_url overrides;
                otherwise renders a styled mock hero matching the screenshot
                (gradient dark backdrop + floating dashboard cards + tagline +
                "Platform v1.0" badge + 19+/500+/99.95% trust tiles). */}
            <div className={`hidden lg:flex relative overflow-hidden flex-col ${(brand.loginImageUrl || !brandResolved) ? "bg-muted" : "bg-gradient-to-br from-violet-950 via-indigo-950 to-slate-950"}`}
                 data-testid="login-hero-panel"
                 data-hero-state={brand.loginImageUrl ? "image" : (brandResolved ? "default" : "loading")}>
                {brand.loginImageUrl ? (
                    <BrandHeroImage src={brand.loginImageUrl} testId="login-hero-image" />
                ) : !brandResolved ? (
                    /* Branding still loading → neutral panel (no gradient flash) */
                    <div className="absolute inset-0 bg-muted" data-testid="login-hero-loading" />
                ) : (
                    <>
                        {/* Top-right "Platform v1.0" badge */}
                        <div className="absolute top-6 right-6 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/10 border border-white/15 text-[10px] uppercase tracking-wider text-white/80 font-mono" data-testid="login-platform-badge">
                            <Shield className="h-3 w-3" />
                            Platform v1.0
                        </div>

                        {/* Floating gradient cards — purely CSS, no images */}
                        <div className="absolute inset-0 overflow-hidden pointer-events-none">
                            {/* Card 1 */}
                            <div className="absolute top-[10%] left-[30%] w-[420px] h-[140px] rounded-2xl bg-gradient-to-br from-white/[0.18] to-white/[0.04] border border-white/15 backdrop-blur-md shadow-2xl rotate-[-2deg]" />
                            {/* Card 2 — slightly forward */}
                            <div className="absolute top-[25%] left-[36%] w-[380px] h-[160px] rounded-2xl bg-gradient-to-br from-violet-400/30 to-violet-700/20 border border-violet-300/25 backdrop-blur-md shadow-2xl rotate-[-3deg]" />
                            {/* Card 3 — donut + sparkline */}
                            <div className="absolute top-[44%] left-[42%] w-[300px] h-[180px] rounded-2xl bg-gradient-to-br from-violet-300/25 to-violet-600/15 border border-violet-200/20 backdrop-blur-md shadow-2xl rotate-[1deg] p-5 flex items-center gap-4">
                                <div className="w-20 h-20 rounded-full border-[10px] border-violet-200/40 border-l-violet-300/80 border-t-violet-300/80 rotate-[35deg]" />
                                <div className="flex-1 space-y-1.5">
                                    <div className="h-2 w-3/4 rounded bg-violet-200/40" />
                                    <div className="h-2 w-1/2 rounded bg-violet-200/30" />
                                    <div className="h-2 w-2/3 rounded bg-violet-200/30" />
                                </div>
                            </div>
                            {/* Glow trail */}
                            <div className="absolute right-[-10%] top-[40%] w-[60%] h-[40%] rounded-full bg-violet-500/20 blur-3xl" />
                            <div className="absolute left-[-5%] bottom-[10%] w-[40%] h-[30%] rounded-full bg-indigo-500/15 blur-3xl" />
                        </div>

                        <div className="relative z-0 mt-auto p-12 text-white">
                            <h2 className="text-4xl xl:text-5xl font-semibold tracking-tight leading-[1.05] max-w-lg mb-3" data-testid="login-hero-heading">
                                Data that drives
                                <br />smarter <span className="font-serif-display italic font-normal">decisions.</span>
                            </h2>
                            <p className="text-sm text-white/70 max-w-md mb-6">
                                {brand.appName} unifies dashboards, data, and AI insights so teams can move faster and achieve more.
                            </p>
                            <div className="grid grid-cols-3 gap-6 max-w-md pt-5 border-t border-white/20">
                                <div className="flex items-start gap-2.5">
                                    <div className="h-9 w-9 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center shrink-0"><Shield className="h-4 w-4" /></div>
                                    <div><div className="kpi-number text-xl text-white">19+</div><div className="text-[10px] uppercase tracking-widest text-white/60">Connectors</div></div>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <div className="h-9 w-9 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center shrink-0"><BadgeCheck className="h-4 w-4" /></div>
                                    <div><div className="kpi-number text-xl text-white">500+</div><div className="text-[10px] uppercase tracking-widest text-white/60">Enterprises</div></div>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <div className="h-9 w-9 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center shrink-0"><Award className="h-4 w-4" /></div>
                                    <div><div className="kpi-number text-xl text-white">99.95%</div><div className="text-[10px] uppercase tracking-widest text-white/60">Uptime SLA</div></div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <Dialog open={!!activeSession} onOpenChange={(o) => !o && setActiveSession(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle data-testid="active-session-title">Another session is active</DialogTitle>
                        <DialogDescription>
                            {activeSession?.message || "You’re already signed in on another device."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="text-xs text-muted-foreground">
                        Signing in here will end the other session immediately — any unsaved work on that device may be lost.
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setActiveSession(null)} className="rounded-sm" data-testid="active-session-cancel">
                            Stay on other device
                        </Button>
                        <Button onClick={() => attempt({ force: true })} disabled={loading} className="rounded-sm" data-testid="active-session-force">
                            {loading ? "Switching…" : "Force log out & sign in here"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* iter-120 round-4 follow-up #8 — SSO email-prompt modal.
                User clicks the Microsoft / Google button → we ask for
                their work email so the backend can resolve their tenant
                and look up the right org's SSO config before redirecting. */}
            <Dialog open={!!ssoProvider} onOpenChange={(o) => !o && !ssoLoading && !ssoTosModalOpen && setSsoProvider(null)}>
                <DialogContent
                    className="max-w-md"
                    data-testid="sso-modal"
                    onPointerDownOutside={(e) => { if (ssoTosModalOpen) e.preventDefault(); }}
                    onInteractOutside={(e) => { if (ssoTosModalOpen) e.preventDefault(); }}
                    onEscapeKeyDown={(e) => { if (ssoTosModalOpen) e.preventDefault(); }}
                >
                    <DialogHeader>
                        <DialogTitle data-testid="sso-modal-title">
                            Sign in with {ssoProvider === "google" ? "Google" : "Microsoft"}
                        </DialogTitle>
                        <DialogDescription>
                            Enter your work email so we can redirect you to your organisation's identity provider.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={startSso} className="space-y-3">
                        <div>
                            <label className="eyebrow mb-1.5 block">Work email</label>
                            <input
                                type="email"
                                required
                                autoFocus
                                value={ssoEmail}
                                onChange={(e) => setSsoEmail(e.target.value)}
                                placeholder="you@company.com"
                                className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm"
                                data-testid="sso-modal-email"
                                disabled={ssoLoading}
                            />
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            We'll match your email to your organisation's entity and use the SSO config your admin set up.
                        </div>
                        {/* FIX(iter-121.190): removed the mandatory "I agree to
                            Terms/Privacy" checkbox from the SSO email prompt per
                            request. SSO sign-in is a return-user flow (terms were
                            accepted at signup) and the identity provider handles
                            consent — the checkbox no longer gates Continue. */}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setSsoProvider(null)} disabled={ssoLoading} className="rounded-sm" data-testid="sso-modal-cancel">
                                Cancel
                            </Button>
                            <Button type="submit" disabled={ssoLoading || !ssoEmail} className="rounded-sm" data-testid="sso-modal-continue">
                                {ssoLoading ? "Redirecting…" : `Continue with ${ssoProvider === "google" ? "Google" : "Microsoft"} →`}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* iter-120 round-4 follow-up #13 — Forgot Password modal.
                Replaces the toast-based UX which was easy to miss
                (especially the temp-password fallback when SMTP isn't
                configured). Now the result lives inside the dialog
                with clear success / temp-password / error states and
                a Copy button for the temp password. */}
            <Dialog open={forgotOpen} onOpenChange={(o) => {
                if (!forgotBusy) { setForgotOpen(o); if (!o) setForgotResult(null); }
            }}>
                <DialogContent className="max-w-md" data-testid="forgot-password-modal">
                    <DialogHeader>
                        <DialogTitle data-testid="forgot-password-title">
                            <span className="inline-flex items-center gap-2">
                                <KeyRound className="h-4 w-4 text-primary" /> Reset your password
                            </span>
                        </DialogTitle>
                        <DialogDescription>
                            Enter your account email. We'll email you a secure link to choose a new password. Single-use, expires in 60 minutes.
                        </DialogDescription>
                    </DialogHeader>
                    {!forgotResult ? (
                        <form onSubmit={submitForgot} className="space-y-3" data-testid="forgot-password-form">
                            <div>
                                <label className="eyebrow mb-1.5 block">Account email</label>
                                <input
                                    type="email" required autoFocus
                                    value={forgotEmail}
                                    onChange={(e) => setForgotEmail(e.target.value)}
                                    className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm font-mono"
                                    placeholder="you@company.com"
                                    disabled={forgotBusy}
                                    data-testid="forgot-password-email-input"
                                />
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setForgotOpen(false)} disabled={forgotBusy} className="rounded-sm" data-testid="forgot-password-cancel">Cancel</Button>
                                <Button type="submit" disabled={forgotBusy || !forgotEmail} className="rounded-sm" data-testid="forgot-password-submit">
                                    {forgotBusy ? "Sending…" : "Send reset link"}
                                </Button>
                            </DialogFooter>
                        </form>
                    ) : forgotResult.ok ? (
                        <div className="space-y-4" data-testid="forgot-password-result">
                            {forgotResult.sso_only ? (
                                <div className="p-3 border border-border bg-secondary/40 rounded-sm text-sm" data-testid="forgot-password-sso-notice">
                                    <div className="eyebrow mb-1.5">Single sign-on account</div>
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        {forgotResult.note || "This account signs in via SSO — there's no password to reset. Use the SSO option on the login page."}
                                    </p>
                                </div>
                            ) : forgotResult.email_sent ? (
                                <div className="p-3 border border-primary/30 bg-primary/5 rounded-sm text-sm" data-testid="forgot-password-email-sent">
                                    <div className="eyebrow text-primary mb-1.5">Check your inbox</div>
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        If <b className="font-mono">{forgotResult.email}</b> matches an account, we've sent a password-reset link.
                                        The link expires in 60 minutes and can only be used once.
                                    </p>
                                    <p className="text-[11px] text-muted-foreground mt-2 italic">
                                        Don't see the email? Check your spam folder or ask your admin to verify SMTP is configured.
                                    </p>
                                </div>
                            ) : forgotResult.demo_reset_url ? (
                                <>
                                    <div className="p-3 border border-border bg-secondary/40 rounded-sm text-sm" data-testid="forgot-password-demo-link">
                                        <div className="eyebrow mb-1.5">Demo mode — SMTP not configured</div>
                                        <p className="text-xs text-muted-foreground leading-relaxed mb-2.5">
                                            Entity SMTP isn't configured, so the email couldn't be delivered.
                                            For demo / local testing, the reset link is shown below. In production,
                                            configure SMTP in <b>Settings → SMTP &amp; Email</b> so users receive
                                            this link by email automatically.
                                        </p>
                                        <div className="flex items-stretch gap-2">
                                            <a
                                                href={forgotResult.demo_reset_url}
                                                target="_blank" rel="noreferrer"
                                                className="flex-1 px-3 py-2 bg-background border border-border rounded-sm text-[11px] font-mono break-all underline hover:text-primary"
                                                data-testid="forgot-password-demo-link-anchor"
                                            >
                                                {forgotResult.demo_reset_url}
                                            </a>
                                            <Button
                                                variant="outline" className="rounded-sm h-auto px-3 shrink-0"
                                                data-testid="forgot-password-copy-link"
                                                onClick={() => { navigator.clipboard.writeText(forgotResult.demo_reset_url); toast.success("Reset link copied"); }}
                                            >
                                                Copy
                                            </Button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="p-3 border border-border bg-secondary/40 rounded-sm text-sm">
                                    <div className="eyebrow mb-1.5">Request received</div>
                                    <p className="text-xs text-muted-foreground">
                                        If <b className="font-mono">{forgotResult.email}</b> matches an account, a reset link has been sent.
                                    </p>
                                </div>
                            )}
                            <DialogFooter>
                                <Button onClick={() => { setForgotOpen(false); setForgotResult(null); }} className="rounded-sm" data-testid="forgot-password-done">
                                    Done
                                </Button>
                            </DialogFooter>
                        </div>
                    ) : (
                        <div className="space-y-4" data-testid="forgot-password-error">
                            <div className="p-3 border border-primary/30 bg-primary/5 rounded-sm text-sm">
                                <div className="eyebrow text-primary mb-1.5">Reset failed</div>
                                <p className="text-xs text-muted-foreground">{forgotResult.error}</p>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setForgotResult(null)} className="rounded-sm" data-testid="forgot-password-retry">Try again</Button>
                                <Button onClick={() => { setForgotOpen(false); setForgotResult(null); }} className="rounded-sm">Close</Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
